// 主題：配色取自 Excalidraw 調色盤，筆觸參數比照 Excalidraw 預設。

export interface Swatch {
  /** 線條與強調色 */
  stroke: string;
  /** 節點填色 */
  fill: string;
}

export interface Theme {
  name: string;
  background: string;
  text: string;
  /** 中心主題的線條與填色 */
  root: Swatch;
  /** 第 1 層分支輪流使用的配色；mono 主題只有一色 */
  branches: string[];
  swatches: Record<string, Swatch>;
  /** 第 1 層節點的填色方式（Excalidraw 預設為 hachure） */
  branchFill: 'hachure' | 'solid' | 'none';
  muted: string;
  relation: string;
}

// Excalidraw 調色盤（淺色模式）：stroke 取 shade 4、fill 取 shade 1。
const LIGHT: Record<string, Swatch> = {
  red: { stroke: '#e03131', fill: '#ffc9c9' },
  pink: { stroke: '#c2255c', fill: '#fcc2d7' },
  grape: { stroke: '#9c36b5', fill: '#eebefa' },
  violet: { stroke: '#6741d9', fill: '#d0bfff' },
  blue: { stroke: '#1971c2', fill: '#a5d8ff' },
  cyan: { stroke: '#0c8599', fill: '#99e9f2' },
  teal: { stroke: '#099268', fill: '#96f2d7' },
  green: { stroke: '#2f9e44', fill: '#b2f2bb' },
  yellow: { stroke: '#f08c00', fill: '#ffec99' },
  orange: { stroke: '#e8590c', fill: '#ffd8a8' },
  gray: { stroke: '#495057', fill: '#e9ecef' },
};

// 深色模式：線條提亮、填色壓暗，維持對比。
const DARK: Record<string, Swatch> = {
  red: { stroke: '#ff8787', fill: '#5c1f1f' },
  pink: { stroke: '#f783ac', fill: '#561c33' },
  grape: { stroke: '#e599f7', fill: '#4b1d55' },
  violet: { stroke: '#b197fc', fill: '#30245e' },
  blue: { stroke: '#74c0fc', fill: '#123a5c' },
  cyan: { stroke: '#66d9e8', fill: '#0f3d45' },
  teal: { stroke: '#63e6be', fill: '#0f4033' },
  green: { stroke: '#8ce99a', fill: '#1b4224' },
  yellow: { stroke: '#ffd43b', fill: '#4d3b07' },
  orange: { stroke: '#ffa94d', fill: '#55300c' },
  gray: { stroke: '#ced4da', fill: '#2e3236' },
};

const BRANCH_ORDER = ['blue', 'green', 'red', 'violet', 'orange', 'teal', 'pink', 'yellow', 'cyan', 'grape'];

export const COLOR_NAMES = Object.keys(LIGHT);

export const THEMES: Record<string, Theme> = {
  sketch: {
    name: 'sketch', background: '#ffffff', text: '#1e1e1e',
    root: { stroke: '#1e1e1e', fill: '#ffec99' },
    branches: BRANCH_ORDER, swatches: LIGHT, branchFill: 'hachure', muted: '#868e96', relation: '#495057',
  },
  'sketch-dark': {
    name: 'sketch-dark', background: '#121212', text: '#e3e3e8',
    root: { stroke: '#e3e3e8', fill: '#4d3b07' },
    branches: BRANCH_ORDER, swatches: DARK, branchFill: 'hachure', muted: '#868e96', relation: '#adb5bd',
  },
  mono: {
    name: 'mono', background: '#ffffff', text: '#1e1e1e',
    root: { stroke: '#1e1e1e', fill: '#f1f3f5' },
    branches: ['gray'], swatches: { ...LIGHT, gray: { stroke: '#1e1e1e', fill: '#ffffff' } }, branchFill: 'none', muted: '#868e96', relation: '#495057',
  },
};

export const THEME_NAMES = Object.keys(THEMES);

// 取得主題；名稱不存在時退回預設 sketch。
export function getTheme(name: string | undefined): Theme {
  return THEMES[name ?? 'sketch'] ?? THEMES.sketch!;
}

// 把顏色屬性（色名或 #rrggbb）解析成配色；自訂色以半透明當填色。
export function resolveSwatch(theme: Theme, color: string): Swatch {
  const named = theme.swatches[color];
  if (named) return named;
  return { stroke: color, fill: color + '33' };
}
